import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Body.css";

interface BodyItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Body: React.FC = () => {
  const [data, setData] = useState<BodyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/body`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching body:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: BodyItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: BodyItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Body Parts",
    de: "Körperteile",
    ta: "உடல் பாகங்கள்",
    si: "ශරීර කොටස්",
    hi: "शरीर के अंग",
    ar: "أجزاء الجسم",
    tr: "Vücut Parçaları",
    ru: "Части тела",
    zh: "身体部位",
    fr: "Parties du corps",
    es: "Partes del cuerpo",
  };

  const subtitleMap = {
    en: "Body Parts in German",
    de: "Körperteile auf Deutsch",
    ta: "ஜெர்மன் மொழியில் உடல் பாகங்கள்",
    si: "ජර්මන් භාෂාවෙන් ශරීර කොටස්",
    hi: "जर्मन में शरीर के अंग",
    ar: "أجزاء الجسم بالألمانية",
    tr: "Almanca'da Vücut Parçaları",
    ru: "Части тела на немецком",
    zh: "德语身体部位",
    fr: "Parties du corps en allemand",
    es: "Partes del cuerpo en alemán",
  };

  return (
    <div className="body-page-container">
      <div className="body-page-header">
        <div>
          <h1 className="body-page-title">{titleMap["de"]}</h1>
          <p className="body-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="body-page-loading">Loading...</div>
      ) : (
        <div className="body-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`body-page-card ${
                  isPlaying ? "body-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="body-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="body-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="body-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="body-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="body-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Body;
